import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import ResourceCardList from './ResourceCardList';
import SavedResourcesPanel from './SavedResourcesPanel';
import { useSavedResources } from '../utils/savedResources';
import useIsPhone from '../utils/useIsPhone';
import LineIcon from './LineIcon';

const NAVY      = '#0D1B2A';
const NAVY_MID  = '#132233';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

// Free-text city values (e.g. "Chicago Heights, IL", "Ina, IL") don't carry real geo data --
// this is a simple keyword grouping, not geocoding. Anything not matching one of these
// Chicago-area keywords buckets into "Rest of Illinois".
const CHICAGO_METRO_KEYWORDS = [
  'Chicago', 'Cicero', 'Evanston', 'Oak Park', 'Chicago Heights', 'Glen Ellyn', 'Palatine',
  'River Grove', 'Sugar Grove', 'Oglesby', 'Addison', 'Melrose Park', 'Willowbrook', 'Joliet', 'Lisle',
];
// Returns a stable, language-independent region key -- display text is resolved separately
// via t('resources.finder.regions.<key>') wherever this is rendered, so the value stored in
// `regions` state (and compared against in the filter below) never changes with language.
function regionOf(city) {
  if (!city) return 'restOfIllinois';
  const c = city.toLowerCase();
  return CHICAGO_METRO_KEYWORDS.some(k => c.includes(k.toLowerCase())) ? 'chicagoMetro' : 'restOfIllinois';
}
const REGION_KEYS = ['chicagoMetro', 'restOfIllinois'];

// Keyword search has to cover every field that actually carries the trade, not just
// name/description: `description` is empty on ~95% of school rows, so a name-only match made
// searches like "Diesel" return nothing at all (0 of 11) and "Welding" only 5 of 80 -- even
// though `category` (populated on 100% of rows) and `program_name` (95%) hold exactly the word
// typed, and both are already rendered on the card the search was hiding.
// Matching is deliberately group-level: a hit on a single program surfaces that whole
// institution's group, which hasActiveFilter then force-expands, the same way the category and
// region filters already behave. Applies to unions too -- they show `category` as their trade.
const KEYWORD_TEXT_FIELDS = ['name', 'description', 'category', 'program_name'];
const KEYWORD_LIST_FIELDS = ['programs', 'certifications'];
function matchesKeyword(r, q) {
  if (KEYWORD_TEXT_FIELDS.some(f => (r[f] || '').toLowerCase().includes(q))) return true;
  return KEYWORD_LIST_FIELDS.some(f =>
    Array.isArray(r[f]) && r[f].some(v => String(v ?? '').toLowerCase().includes(q)));
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

// Every filter control shares one height so the filter row lines up.
const CONTROL_HEIGHT = '38px';
const selStyle = {
  height: CONTROL_HEIGHT, padding: '0 12px', fontSize: '0.84rem', backgroundColor: NAVY_MID,
  border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px',
  color: WHITE, outline: 'none', cursor: 'pointer', fontFamily: FONT_BODY,
};
// Phones: every control is a 44px tap target with at least 14px text. Native selects and the
// search input use 16px so iOS Safari doesn't zoom the page when they get focus.
const PHONE_CONTROL = { height: '44px', fontSize: '0.875rem' };
const PHONE_FIELD   = { height: '44px', fontSize: '16px' };

// Multi-select dropdown (checkbox list) for filters that can have many options -- category has
// up to ~19 values, too many to show as inline toggle pills without dominating the filter bar.
// Selecting multiple values is OR logic (Welding + HVAC shows cards from either), matching how
// faceted filters normally read. On mobile the option panel renders inline (static) instead of
// an absolutely-positioned popover, since it's already inside the collapsible mobile filter
// panel and an overlay-on-overlay would be awkward to dismiss on a small screen.
// optionLabel: display text for an option value (values stay language-independent keys).
function MultiSelectDropdown({ label, options, selected, onChange, isMobile, optionLabel = o => o }) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open || isMobile) return;
    const onClickOutside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open, isMobile]);

  const toggleOption = (opt) => {
    onChange(selected.includes(opt) ? selected.filter(o => o !== opt) : [...selected, opt]);
  };

  const summary = selected.length === 0 ? label : selected.length === 1 ? optionLabel(selected[0]) : t('resources.finder.nSelected', { count: selected.length });

  return (
    <div ref={ref} style={{ position: 'relative', width: isMobile ? '100%' : undefined }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          ...selStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
          width: isMobile ? '100%' : undefined, minWidth: isMobile ? undefined : '170px',
          color: selected.length ? AMBER : WHITE,
          borderColor: selected.length ? AMBER : 'rgba(255,255,255,0.12)',
          ...(isPhone ? PHONE_CONTROL : {}),
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{summary}</span>
        <span style={{ fontSize: isPhone ? '12px' : '10px', flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{
          position: isMobile ? 'static' : 'absolute', top: isMobile ? undefined : 'calc(100% + 6px)', left: 0, zIndex: 20,
          marginTop: isMobile ? '8px' : 0,
          backgroundColor: NAVY_MID, border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px',
          padding: '8px', minWidth: isMobile ? undefined : '220px', maxHeight: '260px', overflowY: 'auto',
          boxShadow: isMobile ? 'none' : '0 8px 24px rgba(0,0,0,0.4)',
        }}>
          {selected.length > 0 && (
            <button
              onClick={() => onChange([])}
              style={{
                display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none',
                color: AMBER, fontSize: '0.76rem', fontWeight: 700, padding: '4px 6px 8px',
                cursor: 'pointer', fontFamily: FONT_BODY,
                ...(isPhone ? { minHeight: '44px', fontSize: '0.875rem', padding: '0 6px' } : {}),
              }}
            >
              {t('resources.finder.clearSelection')}
            </button>
          )}
          {options.map(opt => (
            <label
              key={opt}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px', padding: '6px',
                borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem', color: WHITE,
                ...(isPhone ? { minHeight: '44px', fontSize: '0.875rem', lineHeight: 1.3 } : {}),
              }}
            >
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => toggleOption(opt)}
                style={{ accentColor: AMBER, cursor: 'pointer', ...(isPhone ? { width: '18px', height: '18px', flexShrink: 0 } : {}) }}
              />
              {optionLabel(opt)}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// Shared card list for any resource_directory type ('school' | 'union' today).
// Renders name/city/description + Visit website / Contact actions, and logs
// a resource_clicks row per click without blocking the outbound navigation.
// onViewTradePay(tradeName): optional -- if provided, the inline pay snippet becomes a
// clickable link that hands the mapped trade name back to the parent, which is expected to
// switch to the Career Pay Guide tab and deep-link into that trade there.
export default function ResourceFinder({ type, onViewTradePay }) {
  const { t } = useTranslation();
  const heading = type === 'school' ? t('resources.finder.schoolHeading') : t('resources.finder.unionHeading');
  const description = type === 'school' ? t('resources.finder.schoolDescription') : t('resources.finder.unionDescription');
  const [resources, setResources] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  // Category and region support selecting multiple values (OR logic) -- schoolType, price,
  // and sort stay single-select, no clear need for multi there.
  const [categories, setCategories] = useState([]);
  const [schoolType, setSchoolType] = useState('all');
  const [regions, setRegions]     = useState([]);
  const [priceRange, setPriceRange] = useState('all');
  const [sort, setSort]           = useState('name');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const isMobile = useIsMobile();
  const isPhone = useIsPhone();
  const { saved } = useSavedResources();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setCategories([]);
    setSchoolType('all');
    setRegions([]);
    setPriceRange('all');
    setSort('name');
    (async () => {
      const { data, error } = await supabase
        .from('resource_directory')
        .select('*')
        .eq('type', type)
        .eq('is_active', true)
        .order('name', { ascending: true });
      if (cancelled) return;
      setResources(error ? [] : (data || []));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [type]);

  const categoryOptions = [...new Set(resources.map(r => r.category).filter(Boolean))].sort();
  // Union rows never have school_type -- this naturally comes out empty for them, which
  // hides the filter entirely rather than showing a dropdown with only "All" in it.
  const schoolTypes  = [...new Set(resources.map(r => r.school_type).filter(Boolean))].sort();
  const hasCostData  = resources.some(r => r.cost_min != null);

  const filtered = resources.filter(r => {
    const q = search.toLowerCase();
    if (q && !matchesKeyword(r, q)) return false;
    if (categories.length > 0 && !categories.includes(r.category)) return false;
    if (schoolType !== 'all' && r.school_type !== schoolType) return false;
    if (regions.length > 0 && !regions.includes(regionOf(r.city))) return false;
    if (priceRange !== 'all') {
      // Only actively-applied price filters drop null-cost_min rows -- the unfiltered
      // view must never silently lose a card just because its cost wasn't parsed.
      if (r.cost_min == null) return false;
      if (priceRange === 'under5k' && r.cost_min >= 5000) return false;
      if (priceRange === '5k15k' && (r.cost_min < 5000 || r.cost_min > 15000)) return false;
      if (priceRange === '15kplus' && r.cost_min <= 15000) return false;
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'price') {
      const av = a.cost_min, bv = b.cost_min;
      if (av == null && bv == null) return a.name.localeCompare(b.name);
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av !== bv) return av - bv;
    }
    return a.name.localeCompare(b.name);
  });

  const activeFilterCount = [schoolType, priceRange].filter(v => v !== 'all').length + categories.length + regions.length;
  const hasActiveFilter = !!search.trim() || activeFilterCount > 0;

  // Desktop: one row -- search, institution type, category, region, price, then Sort pushed to
  // the right edge. The row wraps on narrower screens and Sort stays right-aligned on whichever
  // line it lands. Phones get the same controls stacked inside the collapsible Filters panel.
  const fixed = isMobile ? { width: '100%' } : { flex: '0 0 auto' };
  const filterControls = (
    <>
      {schoolTypes.length > 0 && (
        <select
          value={schoolType}
          onChange={e => setSchoolType(e.target.value)}
          aria-label={t('resources.finder.allInstitutionTypes')}
          style={{ ...selStyle, ...fixed, ...(isPhone ? PHONE_FIELD : {}) }}
        >
          <option value="all">{t('resources.finder.allInstitutionTypes')}</option>
          {schoolTypes.map(st => <option key={st} value={st}>{st}</option>)}
        </select>
      )}
      {categoryOptions.length > 0 && (
        <div style={fixed}>
          <MultiSelectDropdown
            label={t('resources.finder.allCategories')}
            options={categoryOptions}
            selected={categories}
            onChange={setCategories}
            isMobile={isMobile}
          />
        </div>
      )}
      <div style={fixed}>
        <MultiSelectDropdown
          label={t('resources.finder.allRegions')}
          options={REGION_KEYS}
          selected={regions}
          onChange={setRegions}
          isMobile={isMobile}
          optionLabel={r => t(`resources.finder.regions.${r}`)}
        />
      </div>
      {hasCostData && (
        <select
          value={priceRange}
          onChange={e => setPriceRange(e.target.value)}
          aria-label={t('resources.finder.allPrices')}
          style={{ ...selStyle, ...fixed, ...(isPhone ? PHONE_FIELD : {}) }}
        >
          <option value="all">{t('resources.finder.allPrices')}</option>
          <option value="under5k">{t('resources.finder.under5k')}</option>
          <option value="5k15k">{t('resources.finder.price5kTo15k')}</option>
          <option value="15kplus">{t('resources.finder.price15kPlus')}</option>
        </select>
      )}
      <select
        value={sort}
        onChange={e => setSort(e.target.value)}
        aria-label={t('resources.sortNameAZ')}
        style={{ ...selStyle, ...fixed, marginLeft: isMobile ? 0 : 'auto', ...(isPhone ? PHONE_FIELD : {}) }}
      >
        <option value="name">{t('resources.sortNameAZ')}</option>
        {hasCostData && <option value="price">{t('resources.finder.sortPriceLowHigh')}</option>}
      </select>
    </>
  );

  return (
    <div>
      {heading && (
        <h1 style={{ fontFamily: FONT_HEAD, fontSize: '1.7rem', color: WHITE, letterSpacing: '0.04em', marginBottom: '6px' }}>
          {heading}
        </h1>
      )}
      {description && (
        <p style={{ fontSize: isPhone ? '0.875rem' : '0.8rem', color: isPhone ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.45)', lineHeight: 1.5, marginBottom: '16px', maxWidth: '640px' }}>
          {description}
        </p>
      )}

      {/* Persistent entry point into the saved/comparison list -- no account needed, backed by
          localStorage (see utils/savedResources). Always visible near the top, not tucked
          inside the mobile filter panel, since it isn't a filter. */}
      <div style={{ marginBottom: '14px' }}>
        <button
          onClick={() => setShowSaved(true)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            backgroundColor: saved.length > 0 ? 'rgba(245,166,35,0.12)' : NAVY_MID,
            color: saved.length > 0 ? AMBER : WHITE,
            border: `1px solid ${saved.length > 0 ? 'rgba(245,166,35,0.4)' : 'rgba(255,255,255,0.12)'}`,
            borderRadius: '8px', padding: '8px 14px', fontSize: '0.82rem', fontWeight: 700,
            cursor: 'pointer', fontFamily: FONT_BODY,
            ...(isPhone ? { minHeight: '44px', padding: '0 16px', fontSize: '0.875rem' } : {}),
          }}
        >
          <LineIcon name="star" size={16} filled={saved.length > 0} />
          <span>{t('resources.finder.saved')}{saved.length > 0 ? ` (${saved.length})` : ''}</span>
        </button>
      </div>

      {/* Filters */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            placeholder={t('resources.finder.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              // 200px, not 160: on desktop this input is flex:1 in a row with every other
              // filter control, which squeezed it to ~162px usable -- narrow enough to clip the
              // placeholder now that it names trade/program, worst in Spanish. Still below the
              // width it gets on mobile, so the Filters button stays on the same row there.
              flex: '1 1 220px', minWidth: '200px', height: CONTROL_HEIGHT, padding: '0 12px', fontSize: '0.84rem',
              backgroundColor: NAVY_MID, border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '8px', color: WHITE, outline: 'none', fontFamily: FONT_BODY,
              ...(isPhone ? PHONE_FIELD : {}),
            }}
          />
          {isMobile ? (
            <button
              onClick={() => setFiltersOpen(o => !o)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                backgroundColor: filtersOpen ? AMBER : NAVY_MID,
                color: filtersOpen ? NAVY : WHITE,
                border: `1px solid ${filtersOpen ? AMBER : 'rgba(255,255,255,0.12)'}`,
                borderRadius: '8px', height: CONTROL_HEIGHT, padding: '0 14px', fontSize: '0.84rem', fontWeight: 700,
                cursor: 'pointer', fontFamily: FONT_BODY, whiteSpace: 'nowrap',
                ...(isPhone ? PHONE_CONTROL : {}),
              }}
            >
              {t('resources.finder.filters')}{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''} {filtersOpen ? '▲' : '▼'}
            </button>
          ) : filterControls}
        </div>

        {isMobile && filtersOpen && (
          <div style={{
            display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px',
            padding: '12px', backgroundColor: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px',
          }}>
            {filterControls}
          </div>
        )}
      </div>

      {!loading && (
        <p style={isPhone
          ? { fontSize: '0.875rem', fontWeight: 700, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '12px' }
          : { fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.25)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '12px' }}>
          {t('resources.finder.resultsCount', { count: sorted.length })}
        </p>
      )}

      <div className="resource-finder-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
        {loading && (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2.5rem 0', color: 'rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>
            {t('common.loading')}
          </div>
        )}
        {!loading && sorted.length === 0 && (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2.5rem 0', color: 'rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>
            {t('resources.finder.noResults')}
          </div>
        )}
        {!loading && sorted.length > 0 && (
          <ResourceCardList
            resources={sorted}
            type={type}
            onViewTradePay={onViewTradePay}
            hasActiveFilter={hasActiveFilter}
          />
        )}
      </div>

      {showSaved && <SavedResourcesPanel onClose={() => setShowSaved(false)} />}
    </div>
  );
}
